/**
 * ConfigureColumnsDrawer — right-side sheet for toggling column visibility
 * (TASK 1-5).
 *
 * UX (matches Deel web Jan 2026 64 & 65):
 *   - "Select all" master checkbox: shows "N / total selected".
 *   - Indeterminate state when some (not all) are selected.
 *   - Individual checkbox rows — `alwaysVisible` columns are disabled.
 *   - Reset button → reverts to caller-provided initial visibility.
 *   - Save button → enabled only when state differs from initial (like 65.png).
 *   - Closes on Escape or overlay click.
 *   - Focus trap: Tab cycles within the drawer.
 *
 * Visual references:
 *   C:/PROJECT_GGJ_B2C/MobbinUI/UIPattern B2C/Table&Filter/Deel web Jan 2026 64.png
 *   C:/PROJECT_GGJ_B2C/MobbinUI/UIPattern B2C/Table&Filter/Deel web Jan 2026 65.png
 */

import { X, Check, Minus, RotateCcw } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import type { ConfigureColumnsDrawerProps } from './types';

function ConfigureColumnsDrawer<T extends Record<string, unknown>>({
  columns,
  visibility,
  open,
  onClose,
  onSave,
}: ConfigureColumnsDrawerProps<T>) {
  // Draft state — only committed on Save.
  const [draft, setDraft] = useState<Record<string, boolean>>({ ...visibility });

  // Reset draft whenever the drawer opens with fresh visibility props.
  useEffect(() => {
    if (open) setDraft({ ...visibility });
  }, [open, visibility]);

  // Derive which columns can be toggled.
  const toggleable = useMemo(
    () => columns.filter((c) => !c.alwaysVisible),
    [columns],
  );

  const visibleCount = useMemo(
    () => toggleable.filter((c) => draft[c.key] !== false).length,
    [draft, toggleable],
  );
  const total  = toggleable.length;
  const allOn  = visibleCount === total;
  const noneOn = visibleCount === 0;
  const indeterminate = !allOn && !noneOn;

  // Has anything changed vs. the live visibility?
  const isDirty = useMemo(() => {
    return toggleable.some((c) => draft[c.key] !== visibility[c.key]);
  }, [draft, toggleable, visibility]);

  // Revert draft to original visibility.
  const handleReset = useCallback(() => {
    setDraft({ ...visibility });
  }, [visibility]);

  const handleMasterToggle = () => {
    setDraft((prev) => {
      const next = { ...prev };
      if (allOn) {
        toggleable.forEach((c) => { next[c.key] = false; });
      } else {
        toggleable.forEach((c) => { next[c.key] = true; });
      }
      return next;
    });
  };

  const handleToggle = (key: string) => {
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Keyboard: close on Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Body scroll lock.
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const firstFocusRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => firstFocusRef.current?.focus());
    }
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Configure columns"
        className="fixed right-0 top-0 bottom-0 z-50 flex flex-col w-[320px] sm:w-[360px] shadow-2xl"
        style={{
          background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))',
          borderLeft: '1px solid var(--vscode-border-primary)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'var(--vscode-border-secondary)' }}
        >
          <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
            Configure columns
          </h2>
          <button
            ref={firstFocusRef}
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors hover:bg-[color:var(--vscode-bg-hover,rgba(0,0,0,0.06))]"
            aria-label="Close"
          >
            <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
          </button>
        </div>

        {/* Master checkbox */}
        <div
          className="flex items-center gap-3 px-5 py-3 border-b"
          style={{ borderColor: 'var(--vscode-border-secondary)' }}
        >
          <IndeterminateCheckbox
            checked={allOn}
            indeterminate={indeterminate}
            onChange={handleMasterToggle}
            id="master-col-toggle"
          />
          <label htmlFor="master-col-toggle" className="flex-1 text-[13px] font-medium cursor-pointer" style={{ color: 'var(--vscode-text-primary)' }}>
            Select all
          </label>
          <span className="text-[12px] tabular-nums" style={{ color: 'var(--vscode-text-secondary)' }}>
            {visibleCount} / {total} selected
          </span>
        </div>

        {/* Column list */}
        <ul className="flex-1 overflow-y-auto py-1">
          {columns.map((col) => {
            const alwaysOn  = col.alwaysVisible ?? false;
            const isChecked = alwaysOn ? true : (draft[col.key] !== false);
            return (
              <li key={col.key}>
                <label
                  className="flex items-center gap-3 px-5 py-2.5 cursor-pointer transition-colors"
                  style={{ minHeight: 44 }}
                  onMouseEnter={(e) => { if (!alwaysOn) (e.currentTarget as HTMLElement).style.background = 'var(--vscode-bg-hover,rgba(0,0,0,0.04))'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <IndeterminateCheckbox
                    checked={isChecked}
                    indeterminate={false}
                    disabled={alwaysOn}
                    onChange={() => !alwaysOn && handleToggle(col.key)}
                  />
                  <span className="flex-1 text-[13px]" style={{ color: alwaysOn ? 'var(--vscode-text-secondary)' : 'var(--vscode-text-primary)' }}>
                    {col.header}
                  </span>
                  {alwaysOn && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: 'var(--vscode-bg-tertiary)', color: 'var(--vscode-text-tertiary)' }}>
                      Always on
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>

        {/* Footer actions */}
        <div
          className="flex items-center justify-between gap-3 px-5 py-4 border-t"
          style={{ borderColor: 'var(--vscode-border-secondary)' }}
        >
          <button
            type="button"
            onClick={handleReset}
            disabled={!isDirty}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              border: '1px solid var(--vscode-border-primary)',
              color:  'var(--vscode-text-primary)',
              background: 'transparent',
            }}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>

          <button
            type="button"
            onClick={() => { onSave(draft); onClose(); }}
            disabled={!isDirty}
            className="flex-1 py-2 rounded-lg text-[13px] font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: isDirty ? 'var(--brand-purple, #6C47FF)' : 'var(--vscode-bg-tertiary)',
              color:      isDirty ? 'var(--brand-purple-on, #FFFFFF)' : 'var(--vscode-text-tertiary)',
            }}
          >
            Save
          </button>
        </div>
      </aside>
    </>,
    document.body,
  );
}

export default ConfigureColumnsDrawer;

// ─────────────────────────────────────────────────────────────────────────
// Indeterminate checkbox helper
// ─────────────────────────────────────────────────────────────────────────

interface IndeterminateCheckboxProps {
  checked:       boolean;
  indeterminate: boolean;
  onChange:      () => void;
  disabled?:     boolean;
  id?:           string;
}

function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  disabled,
  id,
}: IndeterminateCheckboxProps) {
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const showTick   = checked && !indeterminate;
  const showMinus  = indeterminate;

  return (
    <span className="relative flex-shrink-0">
      <input
        ref={ref}
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="sr-only"
      />
      <button
        type="button"
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        disabled={disabled}
        onClick={onChange}
        className="w-[18px] h-[18px] rounded border flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2"
        style={{
          background:   (checked || indeterminate) ? 'var(--brand-purple, #6C47FF)' : 'transparent',
          borderColor:  (checked || indeterminate) ? 'var(--brand-purple, #6C47FF)' : 'var(--vscode-border-primary)',
          '--tw-ring-color': 'var(--brand-purple, #6C47FF)',
        } as React.CSSProperties}
      >
        {showTick  && <Check  className="w-3 h-3 text-white" strokeWidth={3} />}
        {showMinus && <Minus  className="w-3 h-3 text-white" strokeWidth={3} />}
      </button>
    </span>
  );
}
